import { useCallback, useEffect, useRef, useState } from 'react';

import { ManagePageProvider } from '../contexts/ManagePageContext';

import { ManagePageFilter } from './ManagePageFilter';
import { ManagePageHeader } from './ManagePageHeader';
import { ManagePageSidePanel } from './ManagePageSidePanel';
import { type AgentRow, ManagePageTable } from './ManagePageTable';

import './ManagePage.css';

const CONTENT_ARIA = 'Content';

/**
 * AI Agent Management page.
 *
 * Renders the page header, filter bar, agent table, and a slide-in
 * {@link ManagePageSidePanel} for the selected agent. The panel pushes the
 * page content via a margin transition while it slides in from the right.
 */
export default function ManagePage() {
  const [isSidePanelOpen, setIsSidePanelOpen] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState<AgentRow | null>(null);
  const pageRowRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const probe = document.createElement('div');
    probe.style.cssText =
      'position:absolute;top:-9999px;width:100px;height:100px;overflow:scroll;visibility:hidden;';
    document.body.appendChild(probe);
    const scrollbarWidth = probe.offsetWidth - probe.clientWidth;
    document.body.removeChild(probe);
    pageRowRef.current?.style.setProperty(
      '--agent-scrollbar-width',
      `${scrollbarWidth}px`
    );
  }, []);

  useEffect(() => {
    const node = pageRowRef.current;
    if (!node) {
      return;
    }
    const setWidthVar = (px: number) => {
      node.style.setProperty('--agent-page-row-width', `${px}px`);
    };
    setWidthVar(node.getBoundingClientRect().width);
    let initial = true;
    let settleTimer: number | undefined;
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) {
        setWidthVar(entry.contentRect.width);
      }
      if (initial) {
        initial = false;
        return;
      }
      // Suppress the page's margin-right transition while the pageRow is
      // being resized (global sidebar pinning) so the panel-open margin
      // doesn't wobble as the var updates each frame.
      node.style.setProperty('--agent-page-transition-duration', '0s');
      window.clearTimeout(settleTimer);
      settleTimer = window.setTimeout(() => {
        node.style.removeProperty('--agent-page-transition-duration');
      }, 80);
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
      window.clearTimeout(settleTimer);
    };
  }, []);

  const handleRowClick = useCallback((agent: AgentRow) => {
    setSelectedAgent(agent);
    setIsSidePanelOpen(true);
  }, []);

  const handleSidePanelClose = useCallback(() => {
    setIsSidePanelOpen(false);
    setSelectedAgent(null);
  }, []);

  return (
    <ManagePageProvider>
      <div className='Agent-ManagePage-pageRow' ref={pageRowRef}>
        <div
          className={`Agent-ManagePage-page${
            isSidePanelOpen ? ' Agent-ManagePage-page--isPanelOpen' : ''
          }`}>
          <ManagePageHeader />

          <div
            className={`Agent-ManagePage-viewControls${
              isSidePanelOpen
                ? ' Agent-ManagePage-viewControls--isPanelOpen'
                : ''
            }`}>
            <ManagePageFilter />
          </div>

          <section
            className={`Agent-ManagePage-body${
              isSidePanelOpen ? ' Agent-ManagePage-body--isPanelOpen' : ''
            }`}
            aria-label={CONTENT_ARIA}>
            <ManagePageTable
              selectedAgent={selectedAgent}
              onRowClick={handleRowClick}
              setIsSidePanelOpen={setIsSidePanelOpen}
            />
          </section>
        </div>

        <div
          className={`Agent-ManagePage-sidePanelWrapper${
            isSidePanelOpen ? ' Agent-ManagePage-sidePanelWrapper--isOpen' : ''
          }`}
          data-open={isSidePanelOpen}>
          {isSidePanelOpen && (
            <ManagePageSidePanel
              agent={selectedAgent}
              isOpen={isSidePanelOpen}
              onClose={handleSidePanelClose}
            />
          )}
        </div>
      </div>
    </ManagePageProvider>
  );
}
